<script setup lang="ts">
import type { ContactValidationFlow } from '~/types/UserData';

const { $i18n, $containment, $kyc } = useNuxtApp();
const { openToaster } = useLayoutStore();
const { handleContactValidationComplete } = useAuthStore();

interface RecoveryOption {
  id: Extract<Containment.PendencyName, 'email' | 'phone'>;
  label: string;
  description: string;
  icon: string;
}

const isLoading = ref(false);
const contactValidationFlow = ref<ContactValidationFlow>(
  {} as ContactValidationFlow,
);

const recoveryOptions = computed<RecoveryOption[]>(() => {
  const options: RecoveryOption[] = [];
  const recoveryFlow = contactValidationFlow.value;

  if (recoveryFlow.active_options?.email && recoveryFlow.email?.length) {
    options.push({
      id: 'email',
      label: $i18n.t('general.email'),
      description: $i18n.t('auth.recover_password.methods.email.description', {
        email: recoveryFlow.email,
      }),
      icon: 'email_message',
    });
  }

  if (recoveryFlow.active_options?.phone && recoveryFlow.final_phone?.length) {
    options.push({
      id: 'phone',
      label: $i18n.t('general.sms'),
      description: $i18n.t('auth.recover_password.methods.phone.description', {
        phone: recoveryFlow.final_phone,
      }),
      icon: 'sms',
    });
  }

  return options;
});

function isRecoveryOptionValid(option: RecoveryOption): boolean {
  if (!option.id) {
    return false;
  }

  const currentOptions = (recoveryOptions.value || []).map(
    (option) => option.id,
  );

  return currentOptions.includes(option.id);
}

async function selectOptions(option: RecoveryOption): Promise<void> {
  if (!isRecoveryOptionValid(option)) {
    return;
  }

  await $containment.handle([{ name: option.id, frequent: true }], async () => {
    await handleContactValidationComplete({ optionId: option.id });
  });
}

async function getContactValidationOptions() {
  const { ApiService } = useApiService();

  isLoading.value = true;

  try {
    const { data } = await ApiService.get(
      `/bff/users/contact/validation?module=${$kyc.current.source}`,
    );

    contactValidationFlow.value = data;
  } catch (error: any) {
    const message =
      error?.message ||
      error?.data?.message ||
      $i18n.t('account.wallet.withdraw_modal.server_error');

    openToaster({ message, type: 'error' });
  } finally {
    isLoading.value = false;
  }
}

const linkToHelpCenter = (): void => {
  navigateTo(localeLink('/help'), {
    open: {
      target: '_blank',
      windowFeatures: {
        width: 860,
        height: 760,
      },
    },
  });
};

watchEffect(() => {
  if (recoveryOptions.value.length === 1) {
    const option = recoveryOptions.value[0];

    if (option) {
      selectOptions(option);
    }
  }
});

onMounted(async () => {
  await getContactValidationOptions();
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="$style.wrapper">
      <div :class="$style.recoverContent">
        <section :class="$style.headerSection">
          <NuxtIcon
            :class="$style.securityIcon"
            name="security"
          />
          <h3>{{ $t('verification.contact.header.title') }}</h3>
          <span :class="$style.headerDescription">
            {{ $t('verification.contact.header.subtitle') }}
          </span>

          <div :class="$style.steps">
            <div :class="$style.stepItem">
              <div :class="[$style.stepIcon, $style.stepIconSuccess]">
                <NuxtIcon
                  :class="$style.iconAlert"
                  name="check"
                />
              </div>
              <span>
                {{ $t('verification.contact.steps.facial_verification') }}
              </span>
            </div>

            <div :class="$style.stepItem">
              <div :class="[$style.stepIcon, $style.stepIconError]">
                <NuxtIcon
                  :class="$style.iconAlert"
                  name="close"
                />
              </div>
              <span>
                {{ $t('verification.contact.steps.contact_verification') }}
              </span>
            </div>
          </div>
        </section>

        <div :class="$style.optionsIntro">
          <strong>{{ $t('verification.contact.options.title') }}</strong>
          <span>
            {{ $t('verification.contact.options.description') }}
          </span>
        </div>

        <div :class="$style.optionsList">
          <button
            v-for="option in recoveryOptions"
            :key="option.id"
            :class="$style.optionItem"
            @click="selectOptions(option)"
          >
            <div :class="$style.optionInfo">
              <NuxtIcon
                :class="$style.itemIcon"
                :name="option.icon"
              />
              <div>
                <p :class="$style.title">{{ option.label }}</p>
                <p
                  :class="$style.description"
                  v-html="option.description"
                />
              </div>
            </div>
            <NuxtIcon
              :class="$style.optionArrow"
              name="arrows/angle_right"
            />
          </button>
        </div>

        <div :class="$style.footer">
          <div :class="$style.footerText">
            {{ $t('verification.contact.footer.validation_problems') }}
            <button
              :class="$style.footerLink"
              @click.prevent="linkToHelpCenter"
            >
              {{ $t('verification.contact.footer.contact_us') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.wrapper {
  @apply w-full;
}

.recoverContent {
  @apply w-full max-w-sm mx-auto rounded-2xl space-y-8 py-6 text-white;

  h3 {
    @apply text-center text-lg font-bold text-texts;
  }
}

.headerSection {
  @apply flex flex-col items-center gap-2;

  .securityIcon {
    @apply text-primary text-[90px];
  }

  .headerDescription {
    @apply text-texts text-xs;
  }

  .steps {
    @apply w-full flex items-center justify-between gap-4 my-2 mt-2;

    .stepItem {
      @apply flex items-center gap-1 text-white font-semibold text-xs;

      .stepIcon {
        @apply w-6 h-6 flex items-center justify-center rounded-full;

        &.stepIconSuccess {
          @apply bg-success;
        }

        &.stepIconError {
          @apply bg-error;
        }

        .iconAlert {
          @apply text-lg text-white;
        }
      }
    }
  }
}

.optionsIntro {
  @apply flex flex-col gap-1 text-texts;

  strong {
    @apply text-sm;
  }

  span {
    @apply text-xs;
  }
}

.optionsList {
  @apply space-y-4;

  .optionItem {
    @apply w-full flex items-center justify-between rounded-lg px-2 py-3 hover:bg-[#2A2A2A] transition;

    .optionInfo {
      @apply flex items-start gap-3 text-left;

      .itemIcon {
        @apply text-4xl text-primary;
      }

      .title {
        @apply text-sm font-semibold text-texts;
      }

      .description {
        @apply text-xs font-normal text-texts;

        span {
          @apply font-semibold;
        }
      }
    }

    .optionArrow {
      @apply text-texts/50;
    }
  }
}

.footer {
  @apply flex flex-col items-center pt-4 border-solid border-t border-texts/40 text-center space-y-2 text-sm;

  .footerText {
    @apply text-auth-texts text-xs;

    .footerLink {
      @apply text-auth-links;
    }
  }
}
</style>
